import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, Modal, PopConfirm, Select, Table } from '@maxtropy/components';
import { Col, Row, Spin, Empty } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  apiV2DataPropertyListByDeviceIdPost,
  V2DataPropertyListByDeviceIdPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { getDimensionOptions } from '@/api/envDevice';
import { v4 as uuidv4 } from 'uuid';

export interface DataPointItem {
  id: string; // 唯一标识
  dataPropertyId?: number;
  dataPropertyName?: string;
  measurementType?: number; // 累积量或瞬时量
  dimension?: string; // 维度字符串，如 ORIGIN, 1M, 15M, 30M, H, D
  indicatorName?: string;
}

interface DataPointConfigModalProps {
  open: boolean;
  setOpen: (v: boolean) => void;
  deviceId: number; // 设备ID，用于获取数据属性
  value?: DataPointItem[];
  onChange?: (value: DataPointItem[]) => void;
}

const DataPointConfigModal: React.FC<DataPointConfigModalProps> = ({ open, setOpen, deviceId, value, onChange }) => {
  const [form] = Form.useForm();
  const [propertyList, setPropertyList] = useState<V2DataPropertyListByDeviceIdPostResponse['list']>([]);
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<DataPointItem[]>([]);

  useEffect(() => {
    if (open) {
      // 初始化数据
      const initData =
        value?.map(item => ({
          ...item,
          id: item.id || uuidv4(),
        })) || [];
      setDataSource(initData);
      form.setFieldValue('dataPoints', initData);

      // 获取设备数据属性
      if (deviceId) {
        setLoading(true);
        apiV2DataPropertyListByDeviceIdPost({ deviceId })
          .then(res => {
            setPropertyList(res.list || []);
          })
          .finally(() => setLoading(false));
      }
    }
  }, [open, deviceId, value]);

  const propertyOptions = useMemo(() => {
    return (propertyList || []).map(item => ({
      label: item.name,
      value: item.id,
      measurementType: item.measurementType,
    }));
  }, [propertyList]);

  const handleAddRow = () => {
    // 先获取当前表单值，保留用户输入
    const currentFormValues = form.getFieldValue('dataPoints') || [];
    const mergedData = dataSource.map((item, index) => ({
      ...item,
      dimension: currentFormValues[index]?.dimension,
      indicatorName: currentFormValues[index]?.indicatorName,
      dataPropertyId: currentFormValues[index]?.dataPropertyId ?? item.dataPropertyId,
    }));

    const newRow: DataPointItem = {
      id: uuidv4(),
      dataPropertyId: undefined,
      dataPropertyName: undefined,
      measurementType: undefined,
      dimension: undefined,
      indicatorName: undefined,
    };
    const newData = [...mergedData, newRow];
    setDataSource(newData);
    form.setFieldValue('dataPoints', newData);
  };

  const handleDelete = (id: string) => {
    const newData = dataSource.filter(item => item.id !== id);
    setDataSource(newData);
    form.setFieldValue('dataPoints', newData);
  };

  const handleOk = () => {
    form.validateFields().then(values => {
      const dataPoints = values.dataPoints?.map((item: any) => {
        const prop = propertyList?.find(p => p.id === item.dataPropertyId);
        return {
          id: item.id,
          dataPropertyId: item.dataPropertyId,
          dataPropertyName: prop?.name,
          measurementType: prop?.measurementType,
          dimension: item.dimension,
          indicatorName: item.indicatorName,
        };
      });
      onChange?.(dataPoints);
      setOpen(false);
    });
  };

  const handleCancel = () => {
    setOpen(false);
    form.resetFields();
  };

  const columns = [
    {
      title: '数据属性',
      dataIndex: 'dataPropertyId',
      width: 200,
      render: (_: any, record: DataPointItem, index: number) => (
        <Form.Item
          name={['dataPoints', index, 'dataPropertyId']}
          rules={[{ required: true, message: '请选择数据属性' }]}
          style={{ marginBottom: 0 }}
        >
          <Select
            placeholder="请选择"
            options={propertyOptions}
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 180 }}
            onChange={val => {
              // 更新measurementType
              const prop = propertyList?.find(p => p.id === val);
              const newData = [...dataSource];
              newData[index] = {
                ...newData[index],
                dataPropertyId: val,
                measurementType: prop?.measurementType,
              };
              setDataSource(newData);
              // 清空维度选择
              form.setFieldValue(['dataPoints', index, 'dimension'], undefined);
            }}
          />
        </Form.Item>
      ),
    },
    {
      title: '维度',
      dataIndex: 'dimension',
      width: 200,
      render: (_: any, record: DataPointItem, index: number) => {
        // 根据measurementType判断维度选项
        const measurementType = dataSource[index]?.measurementType;
        const options = measurementType !== undefined ? getDimensionOptions(measurementType) : [];

        return (
          <Form.Item
            name={['dataPoints', index, 'dimension']}
            rules={[{ required: true, message: '请选择维度' }]}
            style={{ marginBottom: 0 }}
          >
            <Select placeholder="请选择" options={options} allowClear style={{ width: 180 }} />
          </Form.Item>
        );
      },
    },
    {
      title: '指标名称',
      dataIndex: 'indicatorName',
      width: 200,
      render: (_: any, record: DataPointItem, index: number) => (
        <Form.Item
          name={['dataPoints', index, 'indicatorName']}
          rules={[
            { required: true, message: '请输入指标名称' },
            { max: 50, message: '最多50个字符' },
          ]}
          style={{ marginBottom: 0 }}
        >
          <Input placeholder="请输入" maxLength={50} style={{ width: 180 }} />
        </Form.Item>
      ),
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 100,
      render: (_: any, record: DataPointItem, index: number) => (
        <PopConfirm title="确定删除该数据点吗？" onConfirm={() => handleDelete(record.id)}>
          <Button type="link">删除</Button>
        </PopConfirm>
      ),
    },
  ];

  return (
    <Modal title="数据点展示配置" open={open} onCancel={handleCancel} onOk={handleOk} width={800}>
      <Spin spinning={loading}>
        {(propertyList?.length ?? 0) > 0 ? (
          <Form form={form}>
            <Row gutter={8}>
              <Col span={24}>
                <Form.Item
                  name="dataPoints"
                  valuePropName="dataSource"
                  style={{ marginBottom: 0 }}
                  className="formItemTable"
                >
                  <Table rowKey="id" columns={columns} dataSource={dataSource} pagination={false} scroll={{ y: 400 }} />
                </Form.Item>
              </Col>
              <Col span={24} style={{ marginTop: 8 }}>
                <Button type="dashed" ghost icon={<PlusOutlined />} style={{ width: '100%' }} onClick={handleAddRow}>
                  新增数据点
                </Button>
              </Col>
            </Row>
          </Form>
        ) : (
          <Empty description="该设备无数据属性" />
        )}
      </Spin>
    </Modal>
  );
};

export default DataPointConfigModal;
